import { useState, useEffect, useCallback } from 'react';
import { createReport, estimateCommission } from '../api';
import type { PartnerTier } from '../types';

const RATE_LABELS: Record<string, string> = {
  Associate: '3%',
  'Certified Broker': '5%',
  newAccount: '7% (new account incentive)',
};

const inputClass =
  'w-full rounded-lg border border-leather bg-leather/30 px-3 py-2 text-white placeholder-station-muted focus:ring-2 focus:ring-gold focus:border-gold';
const labelClass = 'block text-sm font-medium text-gold/90 mb-1';

interface Props {
  partnerId: number;
  partnerTier: PartnerTier;
  commissionRate?: number;
  onSuccess: () => void;
}

export default function PartnerActivityForm({ partnerId, partnerTier, commissionRate, onSuccess }: Props) {
  const [accountName, setAccountName] = useState('');
  const [caseVolume, setCaseVolume] = useState<number | ''>('');
  const [collectedRevenue, setCollectedRevenue] = useState<number | ''>('');
  const [isNewAccount, setIsNewAccount] = useState(false);
  const [isMultiUnit, setIsMultiUnit] = useState(false);
  const [brandMessagingCompliance, setBrandMessagingCompliance] = useState(false);
  const [estimatedAmount, setEstimatedAmount] = useState<number>(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const revenue = typeof collectedRevenue === 'number' ? collectedRevenue : 0;

  const refreshEstimate = useCallback(async () => {
    if (revenue <= 0) {
      setEstimatedAmount(0);
      return;
    }
    try {
      const result = await estimateCommission({
        tier: partnerTier,
        collected_revenue: revenue,
        is_new_account: isNewAccount,
        ...(commissionRate != null && Number.isFinite(commissionRate) && { commission_rate: commissionRate }),
      });
      setEstimatedAmount(result.amount);
    } catch {
      setEstimatedAmount(0);
    }
  }, [revenue, partnerTier, isNewAccount, commissionRate]);

  useEffect(() => {
    refreshEstimate();
  }, [refreshEstimate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!brandMessagingCompliance) {
      setError('You must confirm Brand Messaging Compliance to submit.');
      return;
    }
    if (!accountName.trim()) {
      setError('Account name is required.');
      return;
    }
    const cases = typeof caseVolume === 'number' ? caseVolume : 0;
    const revenueVal = typeof collectedRevenue === 'number' ? collectedRevenue : 0;
    if (cases < 0 || revenueVal < 0) {
      setError('Case volume and collected revenue must be non-negative.');
      return;
    }
    setSubmitting(true);
    try {
      await createReport(String(partnerId), {
        account_name: accountName.trim(),
        case_volume: cases,
        collected_revenue: revenueVal,
        is_new_account: isNewAccount,
        is_multi_unit: isMultiUnit,
        brand_messaging_compliance: true,
      });
      setAccountName('');
      setCaseVolume('');
      setCollectedRevenue('');
      setIsNewAccount(false);
      setIsMultiUnit(false);
      setBrandMessagingCompliance(false);
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl">
      {error && (
        <div className="rounded-lg bg-red-900/40 border border-red-500/50 text-red-200 text-sm px-4 py-3">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Account name *</label>
          <input
            type="text"
            value={accountName}
            onChange={(e) => setAccountName(e.target.value)}
            className={inputClass}
            required
          />
        </div>
        <div>
          <label className={labelClass}>Case volume</label>
          <input
            type="number"
            min={0}
            value={caseVolume === '' ? '' : caseVolume}
            onChange={(e) => setCaseVolume(e.target.value === '' ? '' : Number(e.target.value))}
            className={inputClass}
          />
        </div>
      </div>

      <div>
        <label className={labelClass}>
          Collected revenue (commission paid only on collected revenue) *
        </label>
        <input
          type="number"
          min={0}
          step={0.01}
          value={collectedRevenue === '' ? '' : collectedRevenue}
          onChange={(e) =>
            setCollectedRevenue(e.target.value === '' ? '' : Number(e.target.value))
          }
          className={inputClass}
        />
      </div>

      <div className="flex flex-wrap gap-6">
        <label className="flex items-center gap-2 cursor-pointer text-station-muted hover:text-gold/90">
          <input
            type="checkbox"
            checked={isNewAccount}
            onChange={(e) => setIsNewAccount(e.target.checked)}
            className="rounded border-leather bg-leather/30 text-gold focus:ring-gold"
          />
          <span className="text-sm">New account (first 6 months) – 7% incentive</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer text-station-muted hover:text-gold/90">
          <input
            type="checkbox"
            checked={isMultiUnit}
            onChange={(e) => setIsMultiUnit(e.target.checked)}
            className="rounded border-leather bg-leather/30 text-gold focus:ring-gold"
          />
          <span className="text-sm">Multi-unit</span>
        </label>
      </div>

      <div className="rounded-xl border border-gold/50 bg-gold-muted p-4">
        <h3 className="text-sm font-semibold text-gold mb-2">Estimated commission</h3>
        <p className="text-white">
          Rate: <strong className="text-gold">{isNewAccount ? RATE_LABELS.newAccount : RATE_LABELS[partnerTier]}</strong>
          {revenue > 0 && (
            <>
              {' '}
              → <strong className="text-gold">${estimatedAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong>
            </>
          )}
        </p>
        <p className="text-xs text-station-muted mt-1">Commission is paid only on collected revenue. No activity = no protection.</p>
      </div>

      <div className="rounded-lg border border-gold/40 bg-leather/40 p-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={brandMessagingCompliance}
            onChange={(e) => setBrandMessagingCompliance(e.target.checked)}
            className="mt-1 rounded border-leather bg-leather/30 text-gold focus:ring-gold"
            required
          />
          <span className="text-sm font-medium text-white">
            I confirm Brand Messaging Compliance for this report. (Required to submit.)
          </span>
        </label>
      </div>

      <button
        type="submit"
        disabled={submitting || !brandMessagingCompliance}
        className="px-4 py-2 rounded-lg bg-gold text-charcoal font-medium hover:bg-gold-light disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {submitting ? 'Submitting…' : 'Submit activity'}
      </button>
    </form>
  );
}
